<template>
  <div
    class="grid-sequence-drawflow-edit"
    :class="{ 'grid-sequence-drawflow-edit--empty': !has_exercises }"
    data-testid="grid_sequence_drawflow_edit"
  >
    <div v-if="!has_exercises && !readonly" class="grid-sequence-drawflow-empty-message mb-3">
      <h3>{{ $t("regimen_edit.click_icon_to_add_exercise") }}</h3>
    </div>
    <v-alert v-else-if="!has_exercises" outlined type="info">
      {{ $t("grid_sequence_step_list.no_exercises_readonly") }}
    </v-alert>

    <div v-show="has_exercises" class="grid-sequence-drawflow-canvas-wrap">
      <div
        ref="drawflow"
        class="parent-drawflow grid-sequence-drawflow-canvas"
        :class="{ 'theme--dark': $vuetify.theme.dark }"
        tabindex="0"
        :aria-label="$t('grid_sequence_visual.canvas')"
        @click.capture="handle_canvas_click"
        @keydown="pan_canvas_with_keyboard"
        @pointerover="handle_canvas_pointer_over"
        @pointerout="handle_canvas_pointer_out"
      />
      <div class="grid-sequence-drawflow-controls">
        <v-btn
          small
          data-testid="grid_sequence_visual_organize"
          @click="organize_graph"
        >
          <v-icon left small>mdi-auto-fix</v-icon>{{ $t("grid_sequence_visual.organize") }}
        </v-btn>
        <div class="grid-sequence-drawflow-zoom-controls">
          <v-btn
            icon
            tile
            small
            :title="$t('grid_sequence_visual.zoom_in')"
            data-testid="grid_sequence_visual_zoom_in"
            @click="zoom_in"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <v-divider />
          <v-btn
            icon
            tile
            small
            :title="$t('grid_sequence_visual.zoom_out')"
            data-testid="grid_sequence_visual_zoom_out"
            @click="zoom_out"
          >
            <v-icon>remove</v-icon>
          </v-btn>
        </div>
      </div>
      <v-fade-transition>
        <div
          v-if="connection_affordance_source_id"
          class="grid-sequence-connection-hint"
        >
          {{ $t("grid_sequence_visual.connection_drag_hint") }}
        </div>
      </v-fade-transition>
    </div>

    <div v-if="!readonly" class="grid-sequence-drawflow-add-control">
      <v-fab-transition>
        <v-tooltip top open-delay="500">
          <template v-slot:activator="{ on }">
            <v-btn
              color="blue"
              dark
              fab
              v-on="on"
              data-testid="grid_sequence_visual_add_step"
              @click="$emit('add-step')"
            >
              <v-icon>add</v-icon>
            </v-btn>
          </template>
          {{ $t("grid_sequence_step_list.add_step") }}
        </v-tooltip>
      </v-fab-transition>
    </div>

    <v-dialog v-model="exercise_list_dialog" max-width="1000">
      <v-card v-if="selected_step">
        <v-card-title>
          {{ $t("grid_sequence_visual.edit_step") }}
          <v-spacer />
          <v-btn icon @click="exercise_list_dialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            :value="selected_step.title"
            :label="$t('grid_sequence_visual.step_title')"
            outlined
            dense
            :readonly="readonly"
            @input="set_selected_step_title"
          />
          <ExerciseListEdit
            :value="selected_step_exercises"
            :readonly="readonly"
            :additional_video_exercises="additional_video_exercises"
            :show_add_actions="false"
            :manage_items="false"
            @input="set_selected_step_exercises"
            @edit="edit_exercise"
            @duplicate="duplicate_exercise"
            @delete="delete_exercise"
            @dragged="$emit('dragged')"
          />
        </v-card-text>
        <v-card-actions v-if="!readonly">
          <v-btn text color="primary" @click="add_exercise">
            <v-icon left>add</v-icon>{{ $t("grid_sequence_step_list.add_exercise") }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="exercise_list_dialog = false">{{ $t("common.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="condition_dialog" max-width="1000">
      <v-card v-if="selected_completion && selected_step">
        <v-card-title>
          {{ $t("grid_sequence_visual.edit_condition") }}
          <v-spacer />
          <v-btn icon @click="condition_dialog = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <GridSequenceCompletionConditionsEdit
            :value="[selected_completion]"
            :fields="condition_fields(selected_step)"
            :completed_exercises="completed_exercises(selected_step)"
            :steps="local_value"
            :step_id="selected_step.id"
            :readonly="readonly"
            :show_target_selector="false"
            :show_priority="false"
            :allow_add_transition="false"
            :allow_remove_transition="false"
            @input="set_selected_completion"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="condition_dialog = false">{{ $t("common.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Drawflow from "drawflow";
import Vue from "vue";
import uuid from "uuid";
import * as dagre from "@dagrejs/dagre";
import { mapState } from "vuex";
import {
  buildConditionFields,
  comparisonSummaryLabels,
  completedExerciseOptions,
  alwaysStepCompletionCondition,
  defaultStepCompletionCondition,
  gridSequenceConditionSummaryHtml,
  isNeverCompletionCondition,
  isReportableExercise,
  neverStepCompletionCondition,
  removeCompletion,
  removeExercise as removeSequenceExercise,
  removeStep,
  sequenceExerciseTitle,
  sequenceStepTitle,
  setCompletionTarget,
  setStepExercises,
} from "../classes/grid_sequence";
import ExerciseListEdit from "./ExerciseListEdit.vue";
import GridSequenceCompletionConditionsEdit from "./GridSequenceCompletionConditionsEdit.vue";

export default {
  name: "GridSequenceDrawflowEdit",
  components: { ExerciseListEdit, GridSequenceCompletionConditionsEdit },
  props: {
    value: { type: Array, required: true },
    visible: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false },
    additional_video_exercises: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      editor: null,
      syncing: false,
      node_info: {},
      positions: {},
      exercise_list_dialog: false,
      condition_dialog: false,
      selected_step_id: null,
      selected_condition_index: null,
      rebuild_pending: false,
      removing_conditions: new Set(),
      initial_layout_complete: false,
      initial_layout_timeout: null,
      connection_refresh_frame: null,
      layout_nodes: null,
      connection_affordance_source_id: null,
      connection_affordance_dragging: false,
    };
  },
  computed: {
    ...mapState(["exercises", "video_exercises"]),
    local_value() {
      return this.value;
    },
    has_exercises() {
      return this.local_value.some((step) => (step.exercises || []).length > 0);
    },
    all_video_exercises() {
      return { ...(this.additional_video_exercises || {}), ...(this.video_exercises || {}) };
    },
    selected_step_index() {
      return this.local_value.findIndex((step) => step.id === this.selected_step_id);
    },
    selected_step() {
      return this.selected_step_index < 0 ? null : this.local_value[this.selected_step_index];
    },
    selected_step_exercises() {
      return this.selected_step ? this.selected_step.exercises : [];
    },
    selected_completion() {
      if (!this.selected_step || this.selected_condition_index === null) return null;
      return this.selected_step.completion_conditions[this.selected_condition_index] || null;
    },
  },
  watch: {
    value: {
      deep: true,
      handler() {
        this.schedule_rebuild();
      },
    },
    visible(visible) {
      if (visible) this.schedule_rebuild();
    },
    readonly() {
      if (this.editor) {
        this.editor.editor_mode = this.readonly ? "view" : "edit";
        if (this.readonly) this.clear_connection_affordance();
      }
    },
  },
  mounted() {
    this.editor = new Drawflow(this.$refs.drawflow, Vue, this);
    this.editor.reroute = true;
    this.editor.zoom = 0.8;
    this.editor.start();
    this.editor.zoom_refresh();
    this.editor.editor_mode = this.readonly ? "view" : "edit";
    this.editor.on("connectionStart", this.connection_started);
    this.editor.on("connectionCancel", this.connection_cancelled);
    this.editor.on("connectionCreated", this.connection_created);
    this.editor.on("connectionRemoved", this.connection_removed);
    this.editor.on("nodeRemoved", this.node_removed);
    this.rebuild();
  },
  beforeDestroy() {
    if (this.editor) this.capture_positions();
    if (this.initial_layout_timeout !== null) clearTimeout(this.initial_layout_timeout);
    if (this.connection_refresh_frame !== null) cancelAnimationFrame(this.connection_refresh_frame);
    this.clear_connection_affordance();
    this.editor = null;
  },
  methods: {
    schedule_rebuild() {
      if (!this.editor || this.rebuild_pending) return;
      this.rebuild_pending = true;
      this.$nextTick(() => {
        this.rebuild_pending = false;
        this.rebuild();
      });
    },
    escape_html(value) {
      const element = document.createElement("div");
      element.textContent = String(value || "");
      return element.innerHTML;
    },
    node_key(info) {
      if (info.kind === "condition") return `condition:${info.step_id}:${info.condition_index}`;
      return info.kind === "step" ? `step:${info.step_id}` : info.kind;
    },
    capture_positions() {
      if (!this.editor) return;
      const exported = this.editor.export();
      const nodes = exported?.drawflow?.Home?.data || {};
      Object.values(nodes).forEach((node) => {
        const info = this.node_info[node.id];
        if (info) this.positions[this.node_key(info)] = { x: node.pos_x, y: node.pos_y };
      });
    },
    add_node(info, inputs, outputs, fallback, html, className) {
      const key = this.node_key(info);
      const position = this.positions[key] || fallback;
      const id = this.editor.addNode(info.kind, inputs, outputs, position.x, position.y, className, info, html);
      this.node_info[id] = info;
      return id;
    },
    set_node_position(id, x, y) {
      const node = this.$refs.drawflow.querySelector(`#node-${id}`);
      const data = this.editor.drawflow.drawflow.Home.data[id];
      if (!node || !data) return;
      node.style.left = `${x}px`;
      node.style.top = `${y}px`;
      data.pos_x = x;
      data.pos_y = y;
      const info = this.node_info[id];
      if (info) this.positions[this.node_key(info)] = { x, y };
    },
    node_size(id) {
      const node = this.$refs.drawflow.querySelector(`#node-${id}`);
      return {
        width: node ? node.offsetWidth : 260,
        height: node ? node.offsetHeight : 100,
      };
    },
    auto_layout(startNode, stepNodes, conditionNodes, completeNode) {
      const graph = new dagre.graphlib.Graph({ multigraph: true });
      graph.setGraph({
        rankdir: "LR",
        ranksep: 100,
        nodesep: 60,
        edgesep: 30,
        marginx: 40,
        marginy: 40,
        acyclicer: "greedy",
        ranker: "network-simplex",
      });
      graph.setDefaultEdgeLabel(() => ({}));

      const allNodeIds = [
        startNode,
        ...Object.values(stepNodes),
        ...Object.values(conditionNodes).flat(),
        completeNode,
      ];
      allNodeIds.forEach((id) => {
        const size = this.node_size(id);
        graph.setNode(String(id), size);
      });

      if (this.local_value.length > 0) {
        graph.setEdge(String(startNode), String(stepNodes[this.local_value[0].id]), {}, "start");
      }
      let completeConnected = false;
      this.local_value.forEach((step) => {
        (conditionNodes[step.id] || []).forEach((conditionNode) => {
          const info = this.node_info[conditionNode];
          const completion = step.completion_conditions[info.condition_index];
          graph.setEdge(String(stepNodes[step.id]), String(conditionNode), {}, `condition-${conditionNode}`);
          const targetNode = completion.target_step === "complete"
            ? completeNode
            : stepNodes[completion.target_step];
          if (completion.target_step === "complete") completeConnected = true;
          if (targetNode) {
            graph.setEdge(String(conditionNode), String(targetNode), {}, `target-${conditionNode}`);
          }
        });
      });
      if (!completeConnected && this.local_value.length > 0) {
        const lastStep = this.local_value[this.local_value.length - 1];
        const lastConditions = conditionNodes[lastStep.id] || [];
        const anchorNode = lastConditions[lastConditions.length - 1] || stepNodes[lastStep.id];
        graph.setEdge(String(anchorNode), String(completeNode), { weight: 0 }, "complete-layout");
      }

      dagre.layout(graph);
      allNodeIds.forEach((id) => {
        const position = graph.node(String(id));
        const size = this.node_size(id);
        this.set_node_position(id, position.x - size.width / 2, position.y - size.height / 2);
      });
      this.schedule_connection_refresh(allNodeIds);
    },
    organize_graph() {
      if (!this.editor || !this.layout_nodes || !this.has_exercises) return;
      const {
        startNode,
        stepNodes,
        conditionNodes,
        completeNode,
      } = this.layout_nodes;
      this.auto_layout(startNode, stepNodes, conditionNodes, completeNode);
      this.initial_layout_complete = true;
    },
    schedule_initial_layout() {
      if (this.initial_layout_timeout !== null) clearTimeout(this.initial_layout_timeout);
      this.$nextTick(() => {
        if (!this.editor) return;
        if (this.visible) this.organize_graph();
        this.initial_layout_timeout = setTimeout(() => {
          this.initial_layout_timeout = null;
          if (this.visible) this.organize_graph();
        }, 130);
      });
    },
    schedule_connection_refresh(nodeIds) {
      if (this.connection_refresh_frame !== null) {
        cancelAnimationFrame(this.connection_refresh_frame);
      }
      this.connection_refresh_frame = requestAnimationFrame(() => {
        this.connection_refresh_frame = null;
        if (!this.editor) return;
        nodeIds.forEach((id) => this.editor.updateConnectionNodes(`node-${id}`));
      });
    },
    zoom_in() {
      if (this.editor) this.editor.zoom_in();
    },
    zoom_out() {
      if (this.editor) this.editor.zoom_out();
    },
    pan_canvas_with_keyboard(event) {
      if (
        !this.editor
        || event.target !== this.$refs.drawflow
        || event.altKey
        || event.ctrlKey
        || event.metaKey
      ) return;
      const movement = {
        ArrowUp: [0, 40],
        ArrowLeft: [40, 0],
        ArrowDown: [0, -40],
        ArrowRight: [-40, 0],
      }[event.key];
      if (!movement) return;
      event.preventDefault();
      this.editor.canvas_x += movement[0];
      this.editor.canvas_y += movement[1];
      this.editor.precanvas.style.transform = `translate(${this.editor.canvas_x}px, ${this.editor.canvas_y}px) scale(${this.editor.zoom})`;
      this.editor.dispatch("translate", {
        x: this.editor.canvas_x,
        y: this.editor.canvas_y,
      });
    },
    rebuild() {
      if (!this.editor) return;
      this.clear_connection_affordance();
      this.capture_positions();
      this.syncing = true;
      this.editor.clearModuleSelected();
      this.node_info = {};
      const stepNodes = {};
      const conditionNodes = {};
      const startNode = this.add_node(
        { kind: "start" },
        0,
        1,
        { x: 40, y: 80 },
        `<div class="grid-sequence-start-node"><strong>${this.escape_html(this.$t("grid_sequence_visual.start"))}</strong></div>`,
        "grid-sequence-start-drawflow-node",
      );
      this.local_value.forEach((step, index) => {
        const title = this.escape_html(this.step_title(step, index));
        const exerciseItems = step.exercises
          .map((exercise) => `<li>${this.escape_html(this.exercise_title(exercise))}</li>`)
          .join("");
        const deleteControl = this.readonly ? "" : `
          <button class="grid-sequence-node-delete" type="button" data-action="delete-step" data-step-id="${step.id}" aria-label="${this.escape_html(this.$t("grid_sequence_step_list.delete_step"))}">×</button>`;
        const controls = this.readonly ? "" : `
          <div class="grid-sequence-node-actions">
            <button type="button" data-action="edit-step" data-step-id="${step.id}">${this.escape_html(this.$t("grid_sequence_visual.edit_step"))}</button>
          </div>`;
        const html = `<div class="grid-sequence-step-node">
          <div class="grid-sequence-node-header">
            <strong>${title}</strong>
            ${deleteControl}
          </div>
          <div class="grid-sequence-exercise-heading">${this.escape_html(this.$t("common.exercises"))}</div>
          <ul class="grid-sequence-exercise-list">${exerciseItems}</ul>
          ${controls}
        </div>`;
        stepNodes[step.id] = this.add_node(
          { kind: "step", step_id: step.id },
          1,
          1,
          { x: 400 + index * 430, y: 80 + (index % 2) * 240 },
          html,
          "grid-sequence-step-drawflow-node",
        );
      });
      const completeNode = this.add_node(
        { kind: "complete" },
        1,
        0,
        { x: 400 + Math.max(this.local_value.length, 1) * 430, y: 80 },
        `<div class="grid-sequence-complete-node"><strong>${this.escape_html(this.$t("grid_sequence_visual.complete"))}</strong></div>`,
        "grid-sequence-complete-drawflow-node",
      );
      if (this.local_value.length > 0) {
        this.editor.addConnection(startNode, stepNodes[this.local_value[0].id], "output_1", "input_1");
      }
      this.local_value.forEach((step, stepIndex) => {
        conditionNodes[step.id] = [];
        step.completion_conditions.forEach((completion, conditionIndex) => {
          if (completion.target_step === null) return;
          const info = { kind: "condition", step_id: step.id, condition_index: conditionIndex };
          const deleteControl = this.readonly ? "" : `
            <button class="grid-sequence-node-delete" type="button" data-action="delete-condition" data-step-id="${step.id}" data-condition-index="${conditionIndex}" aria-label="${this.escape_html(this.$t("grid_sequence_visual.delete_condition"))}">×</button>`;
          const editControl = this.readonly ? "" : `
            <div class="grid-sequence-node-actions">
              <button type="button" data-action="edit-condition" data-step-id="${step.id}" data-condition-index="${conditionIndex}">${this.escape_html(this.$t("grid_sequence_visual.edit_conditions"))}</button>
            </div>`;
          const html = `<div class="grid-sequence-condition-node">
            <div class="grid-sequence-node-header">
              <strong>${this.escape_html(this.$t("grid_sequence_completion_condition.priority", { number: conditionIndex + 1 }))}</strong>
              ${deleteControl}
            </div>
            <div class="grid-sequence-node-summary">${this.condition_summary_html(completion.condition, step)}</div>
            ${editControl}
          </div>`;
          const conditionNode = this.add_node(
            info,
            1,
            1,
            { x: 610 + stepIndex * 430, y: 80 + (stepIndex % 2) * 240 + conditionIndex * 130 },
            html,
            "grid-sequence-condition-drawflow-node",
          );
          conditionNodes[step.id].push(conditionNode);
          this.editor.addConnection(stepNodes[step.id], conditionNode, "output_1", "input_1");
          const target = completion.target_step === "complete" ? completeNode : stepNodes[completion.target_step];
          if (target) this.editor.addConnection(conditionNode, target, "output_1", "input_1");
        });
      });
      this.layout_nodes = {
        startNode,
        stepNodes,
        conditionNodes,
        completeNode,
      };
      if (!this.initial_layout_complete && this.visible && this.has_exercises) {
        this.schedule_initial_layout();
      }
      this.syncing = false;
    },
    step_title(step, index) {
      return sequenceStepTitle(step, this.$t("grid_sequence_step_list.step", { number: index + 1 }));
    },
    exercise_title(exercise) {
      return sequenceExerciseTitle(
        exercise,
        this.exercises || {},
        this.all_video_exercises,
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    condition_summary_html(condition, step) {
      return gridSequenceConditionSummaryHtml(
        condition,
        step,
        this.condition_text.bind(this),
        this.$t.bind(this),
      );
    },
    condition_text(condition, step) {
      if (!condition || condition.type === "always") return this.$t("grid_sequence_summary.automatic_transition");
      if (condition.type === "exercise_completed_count") {
        const exercise = step.exercises.find((candidate) => candidate.uuid === condition.exercise_id);
        return this.$t(condition.count === 1
          ? "grid_sequence_summary.exercise_completed_once"
          : "grid_sequence_summary.exercise_completed_count", {
          exercise: exercise ? this.exercise_title(exercise) : this.$t("common.exercise"),
          count: condition.count,
        });
      }
      if (condition.type === "step_live_days") {
        return this.$t(condition.days === 1
          ? "grid_sequence_summary.step_live_one_day"
          : "grid_sequence_summary.step_live_days", { days: condition.days });
      }
      const exerciseId = condition.field.split(".")[0];
      const exercise = step.exercises.find((candidate) => candidate.uuid === exerciseId);
      const labels = comparisonSummaryLabels(
        condition,
        this.condition_fields(step),
        this.$i18n.lang(),
        this.$t.bind(this),
      );
      return this.$t("grid_sequence_summary.comparison", {
        exercise: exercise ? this.exercise_title(exercise) : this.$t("common.exercise"),
        field: labels.field,
        operator: labels.operator,
        value: Array.isArray(condition.value) ? condition.value.join(", ") : String(condition.value),
      });
    },
    condition_fields(step) {
      return buildConditionFields(
        step,
        this.exercises || {},
        this.all_video_exercises,
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    completed_exercises(step) {
      return completedExerciseOptions(
        step,
        this.exercises || {},
        this.all_video_exercises,
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    node_id_from_port(port) {
      const node = port?.closest?.(".drawflow-node");
      return node?.id?.startsWith("node-") ? node.id.slice(5) : null;
    },
    activate_connection_affordance(sourceId) {
      this.clear_connection_affordance();
      if (this.readonly || sourceId === null || sourceId === undefined) return false;
      const sourceKey = String(sourceId);
      const sourceKind = this.node_info[sourceKey]?.kind;
      if (!["start", "step"].includes(sourceKind)) return false;
      const canvas = this.$refs.drawflow;
      const sourceNode = canvas?.querySelector(`#node-${sourceKey}`);
      const sourceOutput = sourceNode?.querySelector(".output");
      if (!sourceOutput) return false;

      sourceOutput.classList.add("grid-sequence-connection-source");
      Object.entries(this.node_info).forEach(([nodeId, info]) => {
        if (
          nodeId === sourceKey
          || (sourceKind === "start"
            ? info.kind !== "step"
            : !["step", "complete"].includes(info.kind))
        ) return;
        canvas.querySelector(`#node-${nodeId} .input`)
          ?.classList.add("grid-sequence-connection-target");
      });
      this.connection_affordance_source_id = sourceKey;
      return true;
    },
    clear_connection_affordance() {
      const canvas = this.$refs.drawflow;
      canvas?.querySelectorAll(
        ".grid-sequence-connection-source, .grid-sequence-connection-target",
      ).forEach((port) => {
        port.classList.remove(
          "grid-sequence-connection-source",
          "grid-sequence-connection-target",
        );
      });
      this.connection_affordance_source_id = null;
      this.connection_affordance_dragging = false;
    },
    handle_canvas_pointer_over(event) {
      if (this.connection_affordance_dragging) return;
      const output = event.target?.closest?.(".output");
      if (!output || !this.$refs.drawflow?.contains(output)) return;
      this.activate_connection_affordance(this.node_id_from_port(output));
    },
    handle_canvas_pointer_out(event) {
      if (this.connection_affordance_dragging) return;
      const output = event.target?.closest?.(".output");
      if (!output || !this.$refs.drawflow?.contains(output)) return;
      if (output.contains(event.relatedTarget)) return;
      this.clear_connection_affordance();
    },
    connection_started(connection) {
      if (!this.activate_connection_affordance(connection.output_id)) return;
      this.connection_affordance_dragging = true;
    },
    connection_cancelled() {
      this.clear_connection_affordance();
    },
    connection_created(connection) {
      this.clear_connection_affordance?.();
      if (this.syncing) return;
      const output = this.node_info[connection.output_id];
      const input = this.node_info[connection.input_id];
      if (!output || !input) return this.schedule_rebuild();
      const target = input.kind === "complete" ? "complete" : input.step_id;
      if (!["step", "complete"].includes(input.kind)) return this.schedule_rebuild();
      if (output.kind === "start") {
        if (input.kind !== "step") return this.schedule_rebuild();
        const stepIndex = this.local_value.findIndex((step) => step.id === input.step_id);
        if (stepIndex <= 0) return this.schedule_rebuild();
        const sequence = this.local_value.slice();
        const [firstStep] = sequence.splice(stepIndex, 1);
        sequence.unshift(firstStep);
        this.commit(sequence, "dragged");
        return;
      }
      if (output.kind === "step") {
        const stepIndex = this.local_value.findIndex((step) => step.id === output.step_id);
        if (stepIndex < 0 || target === output.step_id) return this.schedule_rebuild();
        const sequence = this.local_value.map((step) => ({ ...step }));
        const step = sequence[stepIndex];
        let conditionIndex = step.completion_conditions.findIndex((completion) => completion.target_step === null);
        let completion_conditions = step.completion_conditions.slice();
        const exercise = step.exercises.find(isReportableExercise) || step.exercises[0];
        if (conditionIndex < 0) {
          completion_conditions.push(
            exercise
              ? defaultStepCompletionCondition(exercise)
              : alwaysStepCompletionCondition(),
          );
          conditionIndex = completion_conditions.length - 1;
        }
        const completion = completion_conditions[conditionIndex];
        completion_conditions[conditionIndex] = {
          ...completion,
          condition: isNeverCompletionCondition(completion.condition)
            ? (
              exercise
                ? defaultStepCompletionCondition(exercise)
                : alwaysStepCompletionCondition()
            ).condition
            : completion.condition,
          target_step: target,
        };
        sequence[stepIndex] = { ...step, completion_conditions };
        this.commit(sequence);
        this.selected_step_id = output.step_id;
        this.selected_condition_index = conditionIndex;
        this.condition_dialog = true;
        return;
      }
      if (output.kind === "condition") {
        const stepIndex = this.local_value.findIndex((step) => step.id === output.step_id);
        if (stepIndex < 0 || target === output.step_id) return this.schedule_rebuild();
        this.commit(setCompletionTarget(this.local_value, stepIndex, output.condition_index, target));
        return;
      }
      this.schedule_rebuild();
    },
    connection_removed(connection) {
      if (this.syncing) return;
      const output = this.node_info[connection.output_id];
      const input = this.node_info[connection.input_id];
      if (!output || !input) return;
      const condition = output.kind === "condition" ? output : input.kind === "condition" ? input : null;
      if (!condition) return this.schedule_rebuild();
      const removalKey = `${condition.step_id}:${condition.condition_index}`;
      if (this.removing_conditions.has(removalKey)) return;
      this.removing_conditions.add(removalKey);
      this.$nextTick(() => this.removing_conditions.delete(removalKey));
      const stepIndex = this.local_value.findIndex((step) => step.id === condition.step_id);
      if (stepIndex >= 0) this.commit(removeCompletion(this.local_value, stepIndex, condition.condition_index));
    },
    node_removed(nodeId) {
      if (this.syncing) return;
      const info = this.node_info[nodeId];
      if (!info || info.kind === "condition") return;
      if (info.kind === "complete" || info.kind === "start") {
        this.schedule_rebuild();
        return;
      }
      const stepIndex = this.local_value.findIndex((step) => step.id === info.step_id);
      if (stepIndex >= 0) this.commit(removeStep(this.local_value, stepIndex), "deleted");
    },
    commit(sequence, event) {
      this.$emit("input", sequence);
      if (event) this.$emit(event);
      this.schedule_rebuild();
    },
    handle_canvas_click(event) {
      const action = event.target.closest("[data-action]");
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();
      const stepId = action.dataset.stepId;
      const stepIndex = this.local_value.findIndex((step) => step.id === stepId);
      if (stepIndex < 0) return;
      if (action.dataset.action === "edit-step") {
        this.selected_step_id = stepId;
        this.exercise_list_dialog = true;
      } else if (action.dataset.action === "delete-step") {
        this.commit(removeStep(this.local_value, stepIndex), "deleted");
      } else if (action.dataset.action === "edit-condition") {
        this.selected_step_id = stepId;
        this.selected_condition_index = Number(action.dataset.conditionIndex);
        this.condition_dialog = true;
      } else if (action.dataset.action === "delete-condition") {
        this.commit(removeCompletion(this.local_value, stepIndex, Number(action.dataset.conditionIndex)), "deleted");
      }
    },
    set_selected_step_title(title) {
      const sequence = this.local_value.slice();
      sequence.splice(this.selected_step_index, 1, { ...this.selected_step, title });
      this.commit(sequence);
    },
    set_selected_step_exercises(exercises) {
      const sequence = this.local_value.slice();
      sequence.splice(this.selected_step_index, 1, setStepExercises(this.selected_step, exercises));
      this.commit(sequence);
    },
    add_exercise() {
      const index = this.selected_step_index;
      this.exercise_list_dialog = false;
      if (index >= 0) this.$emit("add-exercise", index);
    },
    edit_exercise(index) {
      const exercise = this.selected_step_exercises[index];
      const stepIndex = this.selected_step_index;
      this.exercise_list_dialog = false;
      if (exercise && stepIndex >= 0) this.$emit("edit-exercise", stepIndex, exercise.uuid);
    },
    duplicate_exercise(index) {
      const exercises = this.selected_step_exercises.slice();
      const copy = JSON.parse(JSON.stringify(exercises[index]));
      if (!copy) return;
      copy.uuid = uuid.v4();
      exercises.splice(index + 1, 0, copy);
      this.set_selected_step_exercises(exercises);
      this.$emit("duplicated");
    },
    delete_exercise(index) {
      const exercise = this.selected_step_exercises[index];
      if (!exercise) return;
      if (this.selected_step_exercises.length === 1) {
        this.exercise_list_dialog = false;
        this.commit(removeStep(this.local_value, this.selected_step_index), "deleted");
        return;
      }
      if (
        isReportableExercise(exercise)
        && this.selected_step_exercises.filter(isReportableExercise).length === 1
      ) {
        messageBus.$emit("error", this.$t("grid_sequence_step_list.cannot_remove_last_reportable"));
        return;
      }
      const sequence = this.local_value.slice();
      sequence.splice(
        this.selected_step_index,
        1,
        removeSequenceExercise(this.selected_step, exercise.uuid, neverStepCompletionCondition()),
      );
      this.commit(sequence, "deleted");
    },
    set_selected_completion(completions) {
      if (!completions.length || !this.selected_step) return;
      const sequence = this.local_value.slice();
      const completion_conditions = this.selected_step.completion_conditions.slice();
      completion_conditions.splice(this.selected_condition_index, 1, completions[0]);
      sequence.splice(this.selected_step_index, 1, { ...this.selected_step, completion_conditions });
      this.commit(sequence);
    },
  },
};
</script>

<style src="drawflow/dist/drawflow.min.css"></style>

<style>
.grid-sequence-drawflow-edit {
  position: relative;
}

.grid-sequence-drawflow-edit--empty {
  min-height: 104px;
  padding-top: 48px;
}

.grid-sequence-drawflow-canvas-wrap {
  position: relative;
}

.grid-sequence-drawflow-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.grid-sequence-drawflow-zoom-controls {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.24);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.theme--dark .grid-sequence-drawflow-zoom-controls {
  border-color: rgba(255, 255, 255, 0.3);
  background: #424242;
}

.grid-sequence-drawflow-canvas {
  position: relative;
  width: 100%;
  height: 620px;
  overflow: hidden;
  border: 2px solid rgba(33, 150, 243, 0.35);
  border-radius: 4px;
  background: #fafafa;
}

.grid-sequence-drawflow-canvas:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.grid-sequence-drawflow-canvas.theme--dark {
  background: #303030;
  border-color: rgba(100, 181, 246, 0.5);
}

.grid-sequence-connection-hint {
  position: absolute;
  bottom: 14px;
  left: 50%;
  z-index: 5;
  padding: 5px 10px;
  border: 1px solid rgba(33, 150, 243, 0.28);
  border-radius: 4px;
  color: #37474f;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  font-size: 12px;
  pointer-events: none;
  transform: translateX(-50%);
}

.theme--dark .grid-sequence-connection-hint {
  border-color: rgba(144, 202, 249, 0.38);
  color: #e3f2fd;
  background: rgba(66, 66, 66, 0.94);
}

.grid-sequence-drawflow-canvas .drawflow-node {
  width: 260px;
  border: 2px solid rgba(33, 150, 243, 0.55);
  background: #fff;
  color: #212121;
}

.grid-sequence-drawflow-canvas.theme--dark .drawflow-node {
  background: #424242;
  color: #fff;
}

.grid-sequence-drawflow-canvas .drawflow-node.selected {
  background: #fff;
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.22);
}

.grid-sequence-drawflow-canvas.theme--dark .drawflow-node.selected {
  background: #424242;
}

.grid-sequence-drawflow-canvas .drawflow-delete {
  display: none !important;
}

.grid-sequence-drawflow-canvas .drawflow-node .input.grid-sequence-connection-target,
.grid-sequence-drawflow-canvas .drawflow-node .output.grid-sequence-connection-source {
  border-color: currentColor;
  color: #1976d2;
  background: #e3f2fd;
}

.grid-sequence-drawflow-canvas .drawflow-node .input.grid-sequence-connection-target {
  color: #2e7d32;
  background: #e8f5e9;
}

.grid-sequence-drawflow-canvas .grid-sequence-connection-source::after,
.grid-sequence-drawflow-canvas .grid-sequence-connection-target::after {
  position: absolute;
  inset: -7px;
  border: 2px solid currentColor;
  border-radius: 50%;
  content: "";
  pointer-events: none;
}

.grid-sequence-drawflow-canvas .grid-sequence-connection-source::after {
  animation: grid-sequence-source-pulse 1.25s ease-out infinite;
}

.grid-sequence-drawflow-canvas .grid-sequence-connection-target::after {
  animation: grid-sequence-target-breathe 1.6s ease-in-out infinite alternate;
}

.grid-sequence-drawflow-canvas.theme--dark .drawflow-node .output.grid-sequence-connection-source {
  color: #90caf9;
  background: #1565c0;
}

.grid-sequence-drawflow-canvas.theme--dark .drawflow-node .input.grid-sequence-connection-target {
  color: #a5d6a7;
  background: #2e7d32;
}

@keyframes grid-sequence-source-pulse {
  from {
    opacity: 0.8;
    transform: scale(0.75);
  }

  to {
    opacity: 0;
    transform: scale(1.25);
  }
}

@keyframes grid-sequence-target-breathe {
  from {
    opacity: 0.4;
    transform: scale(0.88);
  }

  to {
    opacity: 0.9;
    transform: scale(1.05);
  }
}

@media (prefers-reduced-motion: reduce) {
  .grid-sequence-drawflow-canvas .grid-sequence-connection-source::after,
  .grid-sequence-drawflow-canvas .grid-sequence-connection-target::after {
    opacity: 1;
    transform: none;
    animation: none;
  }
}

.grid-sequence-drawflow-canvas .drawflow-node.grid-sequence-condition-drawflow-node {
  width: 220px;
  border-color: rgba(156, 39, 176, 0.55);
}

.grid-sequence-drawflow-canvas .drawflow-node.grid-sequence-complete-drawflow-node {
  width: 150px;
  border-color: rgba(76, 175, 80, 0.65);
}

.grid-sequence-drawflow-canvas .drawflow-node.grid-sequence-start-drawflow-node {
  width: 130px;
  border-color: rgba(0, 150, 136, 0.7);
  text-align: center;
}

.grid-sequence-node-summary {
  margin-top: 8px;
  color: #616161;
  font-size: 12px;
}

.grid-sequence-node-header {
  display: flex;
  align-items: center;
  min-height: 28px;
  margin: -15px -15px 12px;
  padding: 8px 10px 8px 14px;
  border-radius: 3px 3px 0 0;
  background: rgba(33, 150, 243, 0.12);
}

.grid-sequence-condition-node .grid-sequence-node-header {
  background: rgba(156, 39, 176, 0.12);
}

.grid-sequence-node-header strong {
  flex: 1;
}

.grid-sequence-node-delete {
  width: 26px;
  height: 26px;
  margin-left: 8px;
  border: 0;
  border-radius: 50%;
  color: #c62828;
  background: transparent;
  cursor: pointer;
  font-size: 20px;
  line-height: 24px;
}

.grid-sequence-node-delete:hover {
  background: rgba(198, 40, 40, 0.12);
}

.grid-sequence-exercise-heading {
  margin-bottom: 4px;
  color: #616161;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.grid-sequence-exercise-list {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
}

.grid-sequence-exercise-list li + li {
  margin-top: 3px;
}

.theme--dark .grid-sequence-node-summary {
  color: #bdbdbd;
}

.theme--dark .grid-sequence-exercise-heading {
  color: #bdbdbd;
}

.theme--dark .grid-sequence-node-delete {
  color: #ef9a9a;
}

.grid-sequence-node-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.grid-sequence-node-actions button {
  cursor: pointer;
  color: #1976d2;
  font: inherit;
  border: 0;
  border-radius: 3px;
  padding: 4px 6px;
  background: rgba(33, 150, 243, 0.1);
}

.theme--dark .grid-sequence-node-actions button {
  color: #90caf9;
  background: rgba(100, 181, 246, 0.14);
}

.grid-sequence-drawflow-add-control {
  position: absolute;
  right: 12px;
  bottom: 12px;
  z-index: 5;
}

@media (max-width: 600px) {
  .grid-sequence-drawflow-canvas {
    height: 520px;
  }

  .grid-sequence-drawflow-canvas .drawflow-node {
    width: 220px;
  }
}
</style>
